import { Component, HostListener, OnInit } from '@angular/core';
import { DonutChartConfig, LineChartConfig } from '../../core/types/chart-types';
import { BarChartConfig, BarChartDataPoint } from '../../shared/types/chart-types';
import { CustomerServiceInsightsService } from './services/customer-service-insights.service';
import { Subscription } from 'rxjs';
import { MixpanelService } from "../../core/services/mixpanel.service";
import { hasDataFlags, Period, PeriodOptions, LocationInsightsAdvancedFilters, topBottomLocationInsightsRow } from './types/customer-types';
import { delay } from 'lodash';

@Component({
    selector: 'app-customer-service-insights',
    templateUrl: './customer-service-insights.component.html',
    styleUrls: ['./customer-service-insights.component.scss'],
    standalone: false
})
export class CustomerServiceInsightsComponent implements OnInit {

  comparisonChartTitle : string = "Number of Issues Comparison";
  totalIssuesChartTitle : string = "Total Issues";
  serviceMetricsChartTitle : string = "Service Metrics";

  options: (keyof typeof Period)[] = PeriodOptions;
  selectedPeriod: string = '12 Months';
  isOpen: boolean = false;

  lineChartConfig!: LineChartConfig;
  donutChartConfig!: DonutChartConfig;
  issuesComparisonChartConfig!: BarChartConfig;

  gaugeNeedleValue: number = 0;
  comparisonChartData: BarChartDataPoint[] = [];
  comparisonChartLabels: string[] = [];
  serviceMetricsChartData: BarChartDataPoint[] = [];
  serviceMetricsChartLabels: string[] = [];
  totalIssuesData: number[] = [];
  totalIssuesMiddleText: number = 0;
  locationInsightsData:any;
  locationInsightsDataRows: topBottomLocationInsightsRow[] = [];

  hasNoLineChartData: boolean = false;

  //advanced filters
  advancedFilterDataOptions: any[] = []; // passed into advancedFilters as input//[{accountName: 'Test Account', city: 'Test City', state: 'Test State', performanceRating: 90}];
  selectedAdvancedFilters : LocationInsightsAdvancedFilters = {
    "accountName": [],
    "city": [],
    "state": [],
    "performanceRating": [],
  };
  keyInputTypes: { [title: string]: string } = {
    "accountName": 'checkbox',
    "city": 'checkbox',
    "state": 'checkbox',
    "performanceRating": 'radio',
  };
  performanceRatingRanges = [
    { min: 90, max: 100, label: '90-100' },
    { min: 80, max: 89, label: '80-89' },
    { min: 70, max: 79, label: '70-79' },
    { min: 60, max: 69, label: '60-69' },
    { min: 50, max: 59, label: '50-59' },
    { min: 40, max: 49, label: '40-49' },
    { min: 30, max: 39, label: '30-39' },
    { min: 20, max: 29, label: '20-29' },
    { min: 10, max: 19, label: '10-19' },
    { min: 0, max: 9, label: '0-9' }
  ];
  hideAdvancedFilters: boolean = false;

  isLoading: boolean = false;
  hasData: hasDataFlags = {
    locationPerformance: false,
    totalIssues: false,
    issuesComparison: false,
    serviceMetrics: false,
    locationInsights: false
  };

  private subscriptions: Subscription[] = [];

  private dataLoadCount: number = 0;
  private expectedDataCount: number = 4; //number of endpoints called

  constructor(
    private _customerServiceInsightsService: CustomerServiceInsightsService,
    private mixPanelService: MixpanelService,
  ) { }

  ngOnInit(): void {
    this.isLoading = true;
    this.hideAdvancedFilters = true;

    //Set the required configuration options for the charts
    this.setChartsConfigOptions();
    this.subscribeToData();
    this.mixPanelService.track('Program Hub Dashboard Opened', {});

  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private resetAllChartData(): void {
    // Reset line chart
    this.lineChartConfig = {
      ...this.lineChartConfig,
      xAxis: [],
      yAxis: []
    };

    // Reset other chart data
    this.gaugeNeedleValue = 0;
    this.comparisonChartData = [];
    this.comparisonChartLabels = [];
    this.serviceMetricsChartData = [];
    this.serviceMetricsChartLabels = [];
    this.totalIssuesData = [];
    this.totalIssuesMiddleText = 0;
    this.locationInsightsData = null;
    this.locationInsightsDataRows = [];

    // Reset flags
    this.hasData = {
      locationPerformance: false,
      totalIssues: false,
      issuesComparison: false,
      serviceMetrics: false,
      locationInsights: false
    };

    this.hasNoLineChartData = true;
  }

  private subscribeToData(): void {
    // Subscribe to period change
    this.subscriptions.push(
      this._customerServiceInsightsService.periodSelectedAndAccountNumbers$.subscribe(
        data => {
          let periodSelectedKey = Object.keys(Period).find(key => Period[key as keyof typeof Period] === data.period);
          this.selectedPeriod = periodSelectedKey ?? 'Period is Null';
        }
      )
    );

    // Subscribe to location performance data
    this.subscriptions.push(
      this._customerServiceInsightsService.allLocationsPerformanceData$.subscribe(
        data => {
          this.updateLocationPerformanceData(data);
          this.checkLoadingComplete();
        }
      )
    );

    // Subscribe to issues count data
    this.subscriptions.push(
      this._customerServiceInsightsService.allLocationsIssuesCountData$.subscribe(
        data => {
          this.updateIssuesCountData(data);
          this.checkLoadingComplete();
        }
      )
    );

    // Subscribe to total calls data
    this.subscriptions.push(
      this._customerServiceInsightsService.totalCallsByTypeData$.subscribe(
        data => {
          if (data) {
            this.serviceMetricsChartData = data.totalCallsChartData;
            this.serviceMetricsChartLabels = data.totalCallsChartLabels;
            this.hasData.serviceMetrics = data.totalCallsChartData.length > 0;
          }
          this.checkLoadingComplete();
        }
      )
    );

    //Subscribe to locationInsights data (top bottom table)
    this.subscriptions.push(
      this._customerServiceInsightsService.locationInsightsData$.subscribe(
        locationData => {
          if (locationData?.statusCode === 'OK') {
            this.locationInsightsData = locationData;
            this.locationInsightsDataRows = locationData.data;
            this.hideAdvancedFilters = true;
            this.createAdvancedFilterData();
            this.hideAdvancedFilters = false;

            this.hasData.locationInsights = true;
          } else {
            this.locationInsightsData = null;
            this.locationInsightsDataRows = [];
            this.hasData.locationInsights = false;
          }
          this.checkLoadingComplete();
        }
      )
    );

    this.subscriptions.push(
      this._customerServiceInsightsService.selectedAdvancedFilters$.subscribe(
        selectedAdvancedFilters => {
          this.selectedAdvancedFilters = selectedAdvancedFilters;
          //this.checkLoadingComplete();
        }
      )
    );

  }

  private checkLoadingComplete(): void {
    this.dataLoadCount++;
    if (this.dataLoadCount === this.expectedDataCount) {
      this.isLoading = false;
      this.dataLoadCount = 0;
    }
  }

  public updateLocationPerformanceData(data: any): void {
    if (data) {
      this.gaugeNeedleValue = data.gaugeNeedleValue;
      this.lineChartConfig = {
        ...this.lineChartConfig,
        xAxis: data.lineChartData.xAxis,
        yAxis: data.lineChartData.yAxis,
      };
      this.hasNoLineChartData = data.lineChartData.yAxis.length === 0;
      this.totalIssuesData = data.totalIssuesData.map(Number);
      this.totalIssuesMiddleText = data.totalIssuesMiddleText;

      //Set the flags to true if location Performance data is available
      this.hasData.locationPerformance = data.gaugeNeedleValue !== 0 && data.lineChartData.yAxis.length > 0;
      this.hasData.totalIssues = data.lineChartData.yAxis.length > 0 && this.totalIssuesMiddleText > 0;
    } else {
      this.hasData.locationPerformance = false;
      this.hasData.totalIssues = false;
    }
  }

  private updateIssuesCountData(data: any): void {
    if (data && data.comparisonChartData?.length > 0) {
      this.comparisonChartData = data.comparisonChartData;
      this.comparisonChartLabels = data.comparisonChartLabels;

      //Set the flag to true if Comparison data is available
      this.hasData.issuesComparison = true;
    } else {
      this.hasData.issuesComparison = false;
    }
  }

  setChartsConfigOptions(): void {
    //Set Options for Line chart configuration
    this.lineChartConfig = {
      xAxis: [],
      yAxis: [],
      xAxisDisplay: true,
      backgroundColor: "rgb(205, 235, 247)",
      borderColor: "rgb(76 172 229)",
      borderWidth: 2,
      yAxisMaxValue: 100,
      yAxisFontSize: 10,
      yAxisPadding: 30,
      yAxisInterval: 20,
      xAxisDisplayBorder: true,
      xAxisDisplayTicks: true,
      yAxisDrawOnChartArea: true,
      fillArea: false,
      showDottedSegment: true,
      pointRadius: 2,
      pointHoverRadius: 4,
      pointBackgroundColor: "rgb(76 172 229)",
      pointBorderColor: "rgb(76 172 229)",
      pointHoverBorderColor: "rgb(76 172 229)",
      applyExtraPadding: true,
      toolTipRequired: true,
      useSimpleTooltip: true,
      mixPanelEventNamePH : 'PH Performance rating line chart hovering data',
      mixPanelEventName : 'Chart is hovered for 2+ seconds'
    }


    //Set Options for Donut chart configuration
    this.donutChartConfig = {
      customFontSize: true,
      middleTextFontSize: 2,
      middleTextFontColor: "#000000",
      rotation: 120,
      circumference: 360,
      aspectRatio: 1,
      cutout: "55%",
      borderRadius: 5
    }

    // Set Options for No. of Issues Bar chart configuration
    this.issuesComparisonChartConfig = {
      yAxisPaddingBottom: 0,
      layoutPaddingLeft: -10,
      xAxisMaxRotation: 0,
    }
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }

  selectOption(option: string): void {
    if (option !== this.selectedPeriod) {
      // Set loading state and reset data
      this.isLoading = true;
      this.hideAdvancedFilters = true;
      this.resetAllChartData();

      // Update selected period
      this.selectedPeriod = option;
      this.isOpen = false;

      // Reset the selectedAdvancedFilters
      this._customerServiceInsightsService.setSelectedAdvancedFilters({
        accountName: [],
        city: [],
        state: [],
        performanceRating: [],
      });
      // Update service
      this._customerServiceInsightsService.setPeriodAndAccountNumbers(
        Period[option as keyof typeof Period],
        []
      );
    } else {
      this.isOpen = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdownContainer = document.querySelector('.timeframe-dropdown-container');

    if (!dropdownContainer?.contains(target)) {
      this.isOpen = false;
    }
  }

  onSelectedFiltersChange(selectedFilters: { [id: string]: string[] }) {
    this.selectedAdvancedFilters = selectedFilters as LocationInsightsAdvancedFilters;
    this._customerServiceInsightsService.setSelectedAdvancedFilters(this.selectedAdvancedFilters);

    let accountNumbers: string[] = [];
    //if there are no advanced filters selected, leave accountNumbers as empty and hide the advanced filters
    //else filter the locationInsights data.
    if(
      selectedFilters['accountName'].length == 0 &&
      selectedFilters['city'].length == 0 &&
      selectedFilters['state'].length == 0 &&
      selectedFilters['performanceRating'].length == 0
    ){
      this.hideAdvancedFilters=true;
    }else{
      //pick the topBottomLocationInsightRows that match all of the selected advanced filters
      //if the length of the advancedFilter option is zero, default to true for that condition.
      this.locationInsightsDataRows.forEach((item) => {
        if(
          (selectedFilters['accountName'].length == 0 || selectedFilters['accountName'].includes(item.accountName)) &&
          (selectedFilters['city'].length == 0 || selectedFilters['city'].includes(item.city)) &&
          (selectedFilters['state'].length == 0 || selectedFilters['state'].includes(item.state)) &&
          (selectedFilters['performanceRating'].length == 0 || selectedFilters['performanceRating'].includes(this.mapNumberToPerformanceRatingRange(item.performanceRating)))
        ){
          accountNumbers.push(item.accountNumber);
        }
      });
    };
    this._customerServiceInsightsService.setAccountNumbers(accountNumbers);
    this.isLoading = true;

    this.resetAllChartData();
  }

  private createAdvancedFilterData() {
    if(this.locationInsightsDataRows.length){
      this.advancedFilterDataOptions = this.locationInsightsDataRows.map((item) => {
        return {
          accountName: item.accountName,
          city: item.city,
          state: item.state,
          performanceRating: this.mapNumberToPerformanceRatingRange(item.performanceRating)//item.performanceRating.toString()
        }
      });
    }
  }

  private mapNumberToPerformanceRatingRange(performanceRating:number) :string {
    for (const range of this.performanceRatingRanges) {
      if (performanceRating >= range.min && performanceRating <= range.max) {
        return range.label;
      }
    }
    return 'Invalid rating';
  }

}
